@if (receipt()?.rejectionComment; as reason) {
  <p-message severity="error" styleClass="mb-4">
    {{ 'Receipt_RejectedReason' | translateWF: 'Rejected from an expense claim:' }} “{{ reason }}”.
    {{ 'Receipt_RejectedReason_Hint' | translateWF: 'Correct it and save to put it back in the pool, or delete it.' }}
  </p-message>
}
@if (!isReview() && receipt()?.missingFields?.length) {
  <p-message severity="warn" styleClass="mb-4">
    {{ 'Receipt_Missing' | translateWF: 'Missing to enter the pool:' }}
    @for (field of receipt()?.missingFields ?? []; track field; let last = $last) {
      {{ missingLabel(field) }}{{ last ? '' : ', ' }}
    }
  </p-message>
}
@if (isReview()) {
  <p-message severity="info" styleClass="mb-4">
    {{
      'Receipt_Review_Hint'
        | translateWF
          : 'Accounting review: the approved vendor, date and amounts stay as they are. Correct the concept, cost center, tax and CAI, and pick the GL account where the rule’s is wrong.'
    }}
  </p-message>
}

<form [formGroup]="form" class="grid grid-cols-12 gap-5">
  <div class="form-box col-span-12 md:col-span-3">
    <label for="receipt-vendor">{{ 'Receipt_Field_Vendor' | translateWF: 'Vendor' }} *</label>
    <p-autocomplete
      inputId="receipt-vendor"
      formControlName="vendor"
      [suggestions]="vendorSuggestions()"
      (completeMethod)="searchVendors($event)"
      optionLabel="name"
      [dropdown]="true"
      [forceSelection]="true"
      [fluid]="true"
      class="w-full"
      styleClass="w-full"
      [placeholder]="'Receipt_Vendor_Placeholder' | translateWF: 'Search a current-expense vendor'"
    >
      <ng-template #item let-vendor>
        <div class="flex flex-col">
          <span>{{ vendor.name }}</span>
          <small class="text-surface-500">{{ vendor.taxId ?? ('Receipt_NoRtn' | translateWF: 'No RTN') }}</small>
        </div>
      </ng-template>
    </p-autocomplete>
    <small class="text-surface-500">
      {{ 'Receipt_Field_Rtn' | translateWF: 'RTN' }}:
      {{ form.controls.vendor.value?.taxId ?? ('Receipt_NoRtn_Hint' | translateWF: 'none — the ISV is added to the expense') }}
    </small>
  </div>
  <div class="form-box col-span-12 md:col-span-3">
    <label for="receipt-date">{{ 'Receipt_Field_Date' | translateWF: 'Date' }} *</label>
    <p-datepicker inputId="receipt-date" formControlName="date" [showIcon]="true" appendTo="body" dateFormat="yy-mm-dd" fluid class="w-full" />
  </div>
  <div class="form-box col-span-12 md:col-span-3">
    <label for="receipt-currency">{{ 'Receipt_Field_Currency' | translateWF: 'Currency' }}</label>
    <p-select inputId="receipt-currency" formControlName="currency" [options]="currencyOptions" optionLabel="label" optionValue="value" styleClass="w-full" />
  </div>
  @if (isHnl()) {
    <div class="form-box col-span-12 md:col-span-3">
      <div class="flex items-center justify-between gap-2">
        <label for="receipt-rate">
          @if (rateOverrideOn()) {
            {{ 'Receipt_Rate_Entered' | translateWF: 'Rate you entered (to USD)' }}
          } @else {
            {{ 'Receipt_Rate_OnFile' | translateWF: 'Rate on file (to USD)' }}
          }
        </label>
        @if (!isReview()) {
          @if (rateOverrideOn()) {
            <a role="button" tabindex="0" class="cs-table-link text-sm" (click)="resetToDayRate()" (keydown.enter)="resetToDayRate()">
              {{ 'Receipt_Rate_UseDayRate' | translateWF: 'Use day’s rate' }}
            </a>
          } @else {
            <a role="button" tabindex="0" class="cs-table-link text-sm" (click)="onOverrideRate()" (keydown.enter)="onOverrideRate()">
              {{ 'Receipt_Rate_Override' | translateWF: 'Override' }}
            </a>
          }
        }
      </div>
      <p-inputnumber
        inputId="receipt-rate"
        formControlName="rateToUsd"
        mode="decimal"
        [minFractionDigits]="2"
        [maxFractionDigits]="6"
        [min]="0"
        [placeholder]="rateLoading() ? '…' : '—'"
        [fluid]="true"
        class="w-full"
        styleClass="w-full"
        inputStyleClass="w-full"
      />
      @if (rateUnavailable() && !rateOverrideOn()) {
        <small class="text-red-500">{{ 'Receipt_Rate_Missing' | translateWF: 'No rate on file for this date. Override it or ask finance to publish it.' }}</small>
      } @else {
        <small class="text-surface-500">
          {{ 'Receipt_Rate_AsOfDate' | translateWF: 'For receipt date' }} {{ rateAsOf() | date: 'dd/MM/yyyy' }}
        </small>
      }
    </div>
  }
  <div class="form-box col-span-12 md:col-span-6 md:col-start-1">
    <label for="receipt-fiscal">{{ 'Receipt_Field_FiscalNumber' | translateWF: 'Fiscal number' }}</label>
    <input id="receipt-fiscal" pInputText class="w-full" formControlName="fiscalNumber" maxlength="40" placeholder="000-001-01-00012345" />
  </div>
  <div class="form-box col-span-12 md:col-span-6">
    <label for="receipt-tax-authorization">{{ 'Receipt_Field_TaxAuthorization' | translateWF: 'Tax authorization (CAI)' }}</label>
    <input id="receipt-tax-authorization" pInputText class="w-full" formControlName="taxAuthorizationNumber" maxlength="60" />
  </div>
</form>

<div class="flex items-center justify-between mt-5 mb-2">
  <span class="font-semibold">{{ 'Receipt_Lines' | translateWF: 'Lines' }}</span>
  @if (isReview()) {
    <p-button
      [label]="'Receipt_GlAccount_Refresh' | translateWF: 'Refresh GL accounts'"
      icon="pi pi-refresh"
      size="small"
      variant="outlined"
      [loading]="refreshing() > 0"
      [pTooltip]="
        'Receipt_GlAccount_Refresh_Tip' | translateWF: 'Asks the GL account rules again with the concept and cost center on screen. Nothing is saved until you save the review.'
      "
      tooltipPosition="left"
      (onClick)="refreshGlAccounts()"
    />
  } @else {
    <p-button [label]="'Receipt_AddLine' | translateWF: 'Add line'" icon="pi pi-plus" size="small" variant="outlined" (onClick)="addLine()" />
  }
</div>

<p-table
  [value]="lines.controls"
  [scrollable]="true"
  [tableStyle]="{ 'min-width': isReview() ? '76rem' : '60rem' }"
  styleClass="cs-datatable-cell-top cs-datatable-cell-fill cs-datatable-numeric-right receipt-lines"
>
  <ng-template pTemplate="header">
    <tr>
      <th style="width: 15rem; min-width: 15rem">{{ 'Receipt_Field_Concept' | translateWF: 'Expense concept' }} *</th>
      <th style="width: 12rem; min-width: 12rem">{{ 'Receipt_Field_Description' | translateWF: 'Description' }}</th>
      <th style="width: 8rem; min-width: 8rem">{{ 'Receipt_Field_TaxRate' | translateWF: 'Tax rate' }}</th>
      <!-- The GL account is Accounting's call: the capturer never sees it, and the cost center takes its width. -->
      @if (isReview()) {
        <th style="width: 15rem; min-width: 15rem">{{ 'Receipt_Field_CostCenter' | translateWF: 'Cost center' }} *</th>
        <th style="width: 17rem; min-width: 17rem">{{ 'Receipt_Field_GlAccount' | translateWF: 'GL account' }}</th>
      } @else {
        <th style="min-width: 15rem">{{ 'Receipt_Field_CostCenter' | translateWF: 'Cost center' }} *</th>
      }
      <th class="text-right" style="width: 10rem; min-width: 10rem">
        {{ 'Receipt_Field_Amount' | translateWF: 'Amount' }} *
        <i
          class="pi pi-info-circle ml-1"
          [pTooltip]="'Receipt_Amount_Tooltip' | translateWF: 'Amount including ISV, as printed on the receipt.'"
          tooltipPosition="top"
          aria-hidden="true"
        ></i>
      </th>
      <th style="width: 3.5rem; min-width: 3.5rem"></th>
    </tr>
  </ng-template>

  <!-- Option A: a line's concept, description and ISV rate span its cost centers; each cost center is its own row
     with its GL account and amount, and a last row adds another cost center or shows the line's total. -->
  <ng-template pTemplate="body" let-rowData let-i="rowIndex">
    @let line = getLineRow(rowData);
    @let splits = splitsOf(line);
    @let footer = !isReview() || splits.length > 1;
    @for (split of splits; track split; let j = $index; let first = $first; let last = $last) {
      <tr [formGroup]="split" [class.receipt-lines__split]="!first" [class.receipt-lines__continued]="!last || footer">
        @if (first) {
          <td [attr.rowspan]="splits.length + (footer ? 1 : 0)" [formGroup]="line">
            <p-select
              [inputId]="'line-concept-' + i"
              formControlName="conceptCode"
              (onChange)="onConceptChange(line)"
              [options]="concepts()"
              optionValue="code"
              [filter]="true"
              filterBy="code,name.es,name.en"
              [showClear]="true"
              appendTo="body"
              [placeholder]="'Receipt_Concept_Placeholder' | translateWF: 'Select'"
              styleClass="w-full"
            >
              <ng-template #selectedItem let-concept>{{ conceptLabel(concept) }}</ng-template>
              <ng-template #item let-concept>{{ conceptLabel(concept) }}</ng-template>
            </p-select>
          </td>
          <td [attr.rowspan]="splits.length + (footer ? 1 : 0)" [formGroup]="line">
            <input [id]="'line-desc-' + i" pInputText class="w-full" formControlName="description" maxlength="200" />
          </td>
          <td [attr.rowspan]="splits.length + (footer ? 1 : 0)" [formGroup]="line">
            <p-select
              [inputId]="'line-tax-' + i"
              formControlName="taxRate"
              [options]="taxRateOptions"
              optionLabel="label"
              optionValue="value"
              [showClear]="true"
              appendTo="body"
              [placeholder]="'Receipt_TaxRate_None' | translateWF: 'No tax'"
              styleClass="w-full"
            />
          </td>
        }
        <td>
          <div class="flex items-center gap-2">
            @if (!first) {
              <i class="pi pi-reply receipt-lines__split-icon" aria-hidden="true"></i>
            }
            <div class="flex-1 min-w-0">
              <app-cost-center-select
                [inputId]="'line-cc-' + i + '-' + j"
                [control]="split.controls.costCenterReference"
                [current]="costCenterOf(split)"
                (costCenterChange)="onCostCenterChange(line, split, $event)"
                [placeholder]="'Receipt_CostCenter_Placeholder' | translateWF: 'Select'"
              />
            </div>
          </div>
        </td>
        @if (isReview()) {
          <td>
            <app-financial-account-select
              [referenceControl]="split.controls.glAccountOverrideReference"
              [postingModule]="pettyCashModule"
              [excludeSubledgerAccounts]="true"
              [placeholder]="ruleAccountOf(split) ?? ('Receipt_GlAccount_Pick' | translateWF: 'Pick the GL account')"
            />
            <small class="text-surface-500">
              @if (hasOverride(split)) {
                {{ 'Receipt_GlAccount_Override' | translateWF: 'Chosen by Accounting' }}
                @if (ruleAccountOf(split); as rule) {
                  · {{ 'Receipt_GlAccount_RuleWas' | translateWF: 'rule' }}: {{ rule }}
                }
              } @else if (ruleAccountOf(split)) {
                @if (isRefreshed(split)) {
                  {{ 'Receipt_GlAccount_Refreshed' | translateWF: 'Refreshed from the rules — save the review to keep it' }}
                } @else {
                  {{ 'Receipt_GlAccount_FromRule' | translateWF: 'From the GL account rules' }}
                }
              } @else {
                <span class="text-red-500">{{ 'Receipt_GlAccount_None' | translateWF: 'No rule matched: pick one' }}</span>
              }
            </small>
          </td>
        }
        <td class="text-right">
          @if (isReview()) {
            <span class="tabular-nums">{{ symbolOf(form.controls.currency.value) }} {{ split.controls.total.value | number: '1.2-2' }}</span>
          } @else {
            <p-inputGroup>
              <p-inputGroupAddon>{{ symbolOf(form.controls.currency.value) }}</p-inputGroupAddon>
              <p-inputnumber
                [inputId]="'line-amount-' + i + '-' + j"
                formControlName="total"
                mode="decimal"
                [minFractionDigits]="2"
                [maxFractionDigits]="2"
                [min]="0"
                inputStyleClass="w-full text-right"
              />
            </p-inputGroup>
          }
        </td>
        <td class="text-center">
          @if (!isReview()) {
            @if (first) {
              <p-button
                icon="pi pi-trash"
                severity="danger"
                variant="text"
                size="small"
                [disabled]="lines.length === 1"
                [ariaLabel]="'Receipt_RemoveLine' | translateWF: 'Delete line'"
                (onClick)="removeLine(i)"
              />
            } @else {
              <p-button
                icon="pi pi-times"
                severity="secondary"
                variant="text"
                size="small"
                [ariaLabel]="'Receipt_RemoveSplit' | translateWF: 'Remove cost center'"
                (onClick)="removeSplit(line, j)"
              />
            }
          }
        </td>
      </tr>
    }
    @if (footer) {
      <tr class="receipt-lines__footer">
        <td>
          @if (!isReview()) {
            <p-button
              [label]="'Receipt_AddSplit' | translateWF: 'Split to another cost center'"
              icon="pi pi-plus"
              variant="text"
              size="small"
              (onClick)="addSplit(line)"
            />
          }
        </td>
        @if (isReview()) {
          <td></td>
        }
        <td class="text-right">
          @if (splits.length > 1) {
            <span class="receipt-lines__line-total tabular-nums">{{ symbolOf(form.controls.currency.value) }} {{ lineTotal(line) | number: '1.2-2' }}</span>
          }
        </td>
        <td></td>
      </tr>
    }
  </ng-template>
</p-table>

<div class="flex justify-end mt-4">
  <dl class="receipt-totals">
    <div class="receipt-totals__row">
      <dt>{{ 'Receipt_Subtotal' | translateWF: 'Subtotal before ISV' }}</dt>
      <dd class="tabular-nums">{{ symbolOf(form.controls.currency.value) }} {{ editorTotal().subtotal | number: '1.2-2' }}</dd>
    </div>
    <div class="receipt-totals__row">
      <dt>{{ 'Receipt_Isv15' | translateWF: 'ISV 15%' }}</dt>
      <dd class="tabular-nums">{{ symbolOf(form.controls.currency.value) }} {{ editorTotal().tax | number: '1.2-2' }}</dd>
    </div>
    <div class="receipt-totals__row receipt-totals__row--total">
      <dt>{{ 'Receipt_Total_WithIsv' | translateWF: 'Total with ISV' }}</dt>
      <dd class="tabular-nums">{{ symbolOf(form.controls.currency.value) }} {{ editorTotal().total | number: '1.2-2' }}</dd>
    </div>
  </dl>
</div>

<div class="flex flex-col gap-2 mt-5">
  <span class="font-semibold">{{ 'Receipt_Attachments' | translateWF: 'Attachments' }}</span>
  @if (receipt(); as current) {
    @for (file of current.attachments; track file.name) {
      <div class="flex items-center gap-2">
        <i class="pi pi-paperclip" aria-hidden="true"></i>
        <a class="cs-table-link" (click)="openAttachment(file.presignedUrl)">{{ file.name.split('/').pop() }}</a>
        @if (!isReview()) {
          <p-button icon="pi pi-times" severity="danger" [text]="true" [rounded]="true" (onClick)="removeAttachment.emit(file.name)" />
        }
      </div>
    } @empty {
      @if (isReview()) {
        <small class="text-surface-500">{{ 'Receipt_Attach_None' | translateWF: 'No files attached.' }}</small>
      }
    }
    @if (!isReview()) {
      <div>
        <p-button [label]="'Receipt_Attach' | translateWF: 'Attach'" icon="pi pi-paperclip" variant="outlined" size="small" (onClick)="attachInput.click()" />
        <input #attachInput type="file" class="hidden" [accept]="accept" (change)="attachFile($event)" />
      </div>
    }
  } @else {
    <small class="text-surface-500">{{ 'Receipt_Attach_AfterSave' | translateWF: 'Save the receipt to attach files (optional).' }}</small>
  }
</div>

<div class="flex justify-end gap-3 mt-5">
  <p-button [label]="'Cancel' | translateWF: 'Cancel'" variant="outlined" (onClick)="cancel()" />
  @if (isReview()) {
    <p-button [label]="'Receipt_Review_Save' | translateWF: 'Save review'" icon="pi pi-check" (onClick)="save(false)" />
  } @else {
    <p-button [label]="'Receipt_Save' | translateWF: 'Save receipt'" variant="outlined" (onClick)="save(false)" />
    <p-button [label]="'Receipt_SaveNext' | translateWF: 'Save and next'" icon="pi pi-arrow-right" (onClick)="save(true)" />
  }
</div>
